import type { Scene } from "@babylonjs/core/scene";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { Light } from "@babylonjs/core/Lights/light";

/** Keep the key, ambient and physical lamp in each reader's bounded material
 * budget. Inclusion alone appends a late lamp behind unrelated accent lights.
 * Own only exclusions added here, including lights delivered after the art. */
export function scopeCanopyReadingLight(scene: Scene, lamp: Light,
  readers: readonly AbstractMesh[]): () => void {
  const allowed = new Set([
    lamp,
    scene.lights.find(light => light.getTypeID() === Light.LIGHTTYPEID_DIRECTIONALLIGHT),
    scene.lights.find(light => light.getTypeID() === Light.LIGHTTYPEID_HEMISPHERICLIGHT),
  ]);
  const additions = new Map<Light, AbstractMesh[]>();
  const exclude = (light: Light): void => {
    if (allowed.has(light)) return;
    const added = readers.filter(mesh => !mesh.isDisposed() && !light.excludedMeshes.includes(mesh));
    if (!added.length) return;
    light.excludedMeshes.push(...added);
    additions.set(light, [...(additions.get(light) ?? []), ...added]);
  };
  for (const light of scene.lights) exclude(light);
  const observer = scene.onNewLightAddedObservable.add(exclude);
  let retired = false;
  return () => {
    if (retired) return;
    retired = true;
    scene.onNewLightAddedObservable.remove(observer);
    for (const [light, added] of additions) {
      light.excludedMeshes = light.excludedMeshes.filter(mesh => !added.includes(mesh));
    }
    additions.clear();
  };
}
